import "./WorkflowTimeline.css";

/**
 * Phase 18 WU-18.F1 — Workflow stepper for the drill-through pages.
 *
 * Renders a horizontal stepper with one node per pipeline step. Each node
 * shows step name, duration, primary operator (avatar initial + name), and
 * an optional breach marker. The `current` flag highlights the in-flight
 * step in Safety Orange.
 *
 * When `slaTargetMs` is provided AND sits within `totalDurationMs`, a
 * vertical SLA marker is drawn at the proportional horizontal position
 * (`slaTargetMs / totalDurationMs * 100%`).
 *
 * Pure presentation — parents fetch the data.
 */
export interface StepNode {
  stepId: string;
  stepName: string;
  durationMs: number;
  operatorId?: string | null;
  operatorName?: string | null;
  breach?: boolean;
  current?: boolean;
}

export interface WorkflowTimelineProps {
  steps: StepNode[];
  slaTargetMs?: number | null;
  totalDurationMs: number;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function initialOf(name?: string | null, fallback?: string | null): string {
  const src = (name ?? fallback ?? "?").trim();
  return src.charAt(0).toUpperCase() || "?";
}

export function WorkflowTimeline({
  steps,
  slaTargetMs,
  totalDurationMs,
}: WorkflowTimelineProps) {
  if (steps.length === 0) {
    return (
      <div className="workflow-timeline__empty" data-testid="workflow-timeline-empty">
        No step trail recorded.
      </div>
    );
  }

  const showSla =
    slaTargetMs != null &&
    slaTargetMs > 0 &&
    totalDurationMs > 0 &&
    slaTargetMs < totalDurationMs;
  const slaLeftPct = showSla ? (slaTargetMs / totalDurationMs) * 100 : 0;

  return (
    <div className="workflow-timeline" data-testid="workflow-timeline">
      {showSla && (
        <div
          className="workflow-timeline__sla-marker"
          data-testid="workflow-timeline-sla-marker"
          style={{ left: `${slaLeftPct}%` }}
          aria-label={`SLA target ${formatDuration(slaTargetMs)}`}
        >
          <span className="workflow-timeline__sla-label">SLA</span>
        </div>
      )}
      <ol className="workflow-timeline__list">
        {steps.map((s, idx) => {
          const classes = [
            "workflow-timeline__node",
            s.current ? "workflow-timeline__node--current" : "",
            s.breach ? "workflow-timeline__node--breach" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li
              key={s.stepId}
              className={classes}
              data-testid={`workflow-timeline-node-${s.stepId}`}
            >
              {idx > 0 && <span className="workflow-timeline__connector" aria-hidden="true" />}
              <div className="workflow-timeline__dot">
                <span className="workflow-timeline__index">{idx + 1}</span>
                {s.breach && (
                  <span
                    className="workflow-timeline__breach-flag"
                    data-testid={`workflow-timeline-breach-${s.stepId}`}
                    aria-label="SLA breach"
                  >
                    !
                  </span>
                )}
              </div>
              <div className="workflow-timeline__body">
                <div className="workflow-timeline__name">{s.stepName}</div>
                <div className="workflow-timeline__duration">{formatDuration(s.durationMs)}</div>
                {(s.operatorName || s.operatorId) && (
                  <div className="workflow-timeline__operator">
                    <span className="workflow-timeline__avatar" aria-hidden="true">
                      {initialOf(s.operatorName, s.operatorId)}
                    </span>
                    <span className="workflow-timeline__operator-name">
                      {s.operatorName ?? s.operatorId}
                    </span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
